/**
 * Utility functions for rounding values to safe Cloudflare image/video sizes.
 */

type Dimension = number | 'auto';

/**
 * Rounds a given number down to the nearest multiple of 10.
 * Example: 352 → 350, 358 → 350, 362 → 360, 376 → 370
 */
export function roundDownToNearestTen(value: number): number {
  return Math.floor(value / 10) * 10;
}

/**
 * Cloudflare-safe width rounding.
 * Accepts either a number or 'auto'.
 */
export function getCloudflareWidth(width: Dimension, min = 100, max = 1200): Dimension {
  if (width === 'auto') return 'auto';
  const rounded = roundDownToNearestTen(width);
  return Math.max(min, Math.min(rounded, max));
}

/**
 * Cloudflare-safe height rounding.
 * Accepts either a number or 'auto'.
 */
export function getCloudflareHeight(height: Dimension, min = 100, max = 1200): Dimension {
  if (height === 'auto') return 'auto';
  const rounded = roundDownToNearestTen(height);
  return Math.max(min, Math.min(rounded, max));
}

/**
 * Convenience helper: returns a combined width/height string
 * for use in Cloudflare image URLs.
 * Example: "width=350,height=auto"
 */
export function getCloudflareSize(width: Dimension, height: Dimension, min = 100, max = 1200): string {
  const w = getCloudflareWidth(width, min, max);
  const h = getCloudflareHeight(height, min, max);
  return `width=${w},height=${h}`;
}
